import { useFormik } from 'formik';
import localforage from 'localforage';
import React, { Fragment, useContext, useState } from 'react';
import { Detector } from 'react-detect-offline';
import { useHistory, useLocation } from 'react-router-dom';

import { deleteRemoveDocument } from '../api/DELETE_RemoveDocument';
import { postAuth } from '../api/POST_Auth';
import { postCreateDocument } from '../api/POST_CreateDocument';
import { postUploadImageAndItem } from '../api/POST_UploadImageAndItem';
import { Body, postCheckUserLark } from '../api/POST_User';
import { postWasteCategory } from '../api/POST_WasteCategory';
import { GlobalContext } from '../App';
import { BodyWrapper } from '../components/BodyWrapper';
import Button from '../components/Button';
import CardCategory from '../components/CardCategory';
import Header from '../components/Header';
import HeaderOutlet from '../components/HeaderOutlet';
import Layout from '../components/Layout';
import Loader from '../components/Loader';
import ModalAccountInfo from '../components/ModalAccountInfo';
import ModalConfirmation from '../components/ModalConfirmation';
import { LocalStorageKeyEnum } from '../enums/LocalStorageKey';
import { dateFormatter } from '../utils/dateFormatter';
import { displayedCategoryName, excludedCategories } from '../utils/wasteCategory';
import { UserInterface } from './index.type';

interface HomePageForm extends UserInterface {
  isModalAccountVisible: boolean;
  isModalInfoVisible: boolean;
  isServerError: boolean;
  buttonDisabled: boolean;
  isOnline: boolean;
  isModalConfirmationVisible: boolean;
  isModalErrorSubmitVisible: boolean;
  isLoadingSubmit: boolean;
}

type PageStatus = 'loading' | 'failed' | 'success';

function DataURIToBlob(dataURI: string) {
  const splitDataURI = dataURI.split(',');
  const byteString =
    splitDataURI[0].indexOf('base64') >= 0 ? atob(splitDataURI[1]) : decodeURI(splitDataURI[1]);
  const mimeString = splitDataURI[0].split(':')[1].split(';')[0];

  const ia = new Uint8Array(byteString.length);
  for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);

  const base64ProductImage = new Blob([ia], { type: mimeString });

  return base64ProductImage;
}

const Home = () => {
  const globalContext = useContext(GlobalContext);
  const [pageStatus, setPageStatus] = useState<PageStatus>('loading');

  const history = useHistory();
  const location = useLocation();
  const cancelButtonRef = React.useRef(null);
  const { values, handleSubmit, setFieldValue } = useFormik<HomePageForm>({
    initialValues: {
      userId: '',
      name: '',
      outletName: '',
      startTime: null,
      outletId: null,
      isModalInfoVisible: false,
      isServerError: false,
      categories: [],
      categoriesLoaded: false,
      isOnline: false,
      buttonDisabled: true,
      isModalAccountVisible: false,
      isModalConfirmationVisible: false,
      isModalErrorSubmitVisible: false,
      isLoadingSubmit: false
    },
    validateOnMount: true,
    onSubmit: async (formValues) => {
      const mapForm = new Map<string, boolean>();
      mapForm.set('isSubmitFailed', false);
      let id = 0;
      try {
        setFieldValue('isLoadingSubmit', true);
        for await (const categories of formValues.categories) {
          if (categories.products.length > 0) {
            const request = {
              idOutlet: formValues?.outletId || 0,
              idWasteCategory: categories?.id || 0,
              userId: formValues?.userId || 'SYSTEM',
              name: formValues?.name || 'SYSTEM'
            };

            const post: any = await postCreateDocument(request);

            if (post.status === 'success') {
              const { insertedId, idWasteCategory } = post.data[0];
              id = insertedId;
              for await (const item of categories.products) {
                const formData = new FormData();

                formData.append('id_product', String(item.product.productId));
                formData.append('id_waste_category', String(idWasteCategory));
                formData.append('qty', String(item.count.quantity));
                formData.append('id_stock_waste_document', String(insertedId));
                formData.append(
                  'expired_date',
                  item.date ? `${item.date.year}-${item.date.month}-${item.date.day}` : null
                );

                if (item?.images?.length) {
                  for await (const imgs of item.images) {
                    formData.append('images', DataURIToBlob(imgs.dataURL));
                  }
                }

                const uploadItem = await postUploadImageAndItem(formData);
                if (uploadItem.status === 'error') {
                  mapForm.set('isSubmitFailed', true);
                  setFieldValue('isModalErrorSubmitVisible', true);
                  break;
                }
              }
            }
          }
        }
      } catch (error) {
        console.error(error);
        id > 0 ? await deleteRemoveDocument(+id) : null;
        mapForm.set('isSubmitFailed', true);
        setFieldValue('isModalErrorSubmitVisible', true);
      } finally {
        setFieldValue('isLoadingSubmit', false);
        toggleModalConfirmationVisible();

        localforage.removeItem(LocalStorageKeyEnum['TOKEN']);
        localforage.removeItem(LocalStorageKeyEnum['USER']);
        localforage.removeItem(LocalStorageKeyEnum['PRODUCTS']);
      }

      if (mapForm.has('isSubmitFailed') && !mapForm.get('isSubmitFailed')) {
        mapForm.clear();
        history.push('success');
      }
    }
  });

  const whitelistState = (state: string) => {
    const decode = decodeURI(state);
    return decode.split('+').join(' ');
  };

  const onCheckUserLark = React.useCallback(
    async (code) => {
      try {
        const body = {
          clientId: process.env.REACT_APP_CLIENT_ID,
          clientSecret: process.env.REACT_APP_CLIENT_SECRET
        };

        const auth = await postAuth(body);
        if (auth.status === 'success') {
          await localforage.setItem(LocalStorageKeyEnum['TOKEN'], auth.data.accessToken);
          const getState: string = new URLSearchParams(location.search).get('state')?.toString();
          const state: string = whitelistState(getState);

          const bodyLark: Body = { code, state };

          const { data, status, messageData } = await postCheckUserLark(bodyLark);
          if (status === 'success') {
            const categories = data.categories.map((category) => {
              return { name: category, products: [] };
            });
            categories.splice(1, 0, { name: 'spoiled', products: [] });
            const startTime = new Date();
            setFieldValue('userId', data.userId);
            setFieldValue('name', data.name);
            setFieldValue('outletId', data.outletId);
            setFieldValue('outletName', data.outletName);
            setFieldValue('startTime', startTime);
            const newUser = {
              userId: data.userId,
              name: data.name,
              outletId: data.outletId,
              outletName: data.outletName,
              startTime,
              categories: categories
            };
            const currentUser = await localforage.getItem<typeof newUser>(
              LocalStorageKeyEnum['USER']
            );
            if (currentUser && currentUser.outletName !== newUser.outletName) {
              await localforage.removeItem(LocalStorageKeyEnum['PRODUCTS']);
            }
            localforage.setItem(LocalStorageKeyEnum['USER'], newUser);
            setPageStatus('success');
          } else {
            if (messageData === 'Form Waste Belum Tersedia') {
              throw new Error();
            } else {
              throw new Error('500');
            }
          }
        }
      } catch (error) {
        console.error(error);
        localforage.removeItem(LocalStorageKeyEnum['USER']);
        if (
          error.code === '500' ||
          error.code === '503' ||
          error.code === '401' ||
          error.message === '500'
        ) {
          setFieldValue('isServerError', true);
        }
        setPageStatus('failed');
      }
    },
    [location.search, setFieldValue]
  );

  React.useEffect(() => {
    if (values.categoriesLoaded) {
      if (window) {
        localforage.getItem(LocalStorageKeyEnum['PRODUCTS'], (_, newProduct) => {
          if (!newProduct) return;
          const payload = [...values.categories];
          Object.keys(newProduct).forEach((keys) => {
            values.categories.map((categories, index) => {
              if (categories.name === keys) {
                payload[index] = {
                  ...categories,
                  products: newProduct[keys].products
                };
              }
            });
          });
          setFieldValue('categories', payload);
        });
      } else {
        const payload = [...values.categories];

        Object.keys(globalContext.products).forEach((keys) => {
          values.categories.map((categories, index) => {
            if (categories.name === keys) {
              payload[index] = {
                ...categories,
                products: globalContext.products[keys].products
              };
            }
          });
        });
        setFieldValue('categories', payload);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.categoriesLoaded]);

  const fetchWasteCategory = async () => {
    const { data } = await postWasteCategory();

    const wasteCategoryList = [];

    data?.map((item) => {
      const wasteCategoryName = displayedCategoryName(item.wasteCategoryName);
      if (!excludedCategories.includes(wasteCategoryName.toLocaleLowerCase())) {
        item.wasteCategoryName = wasteCategoryName;
        wasteCategoryList.push(item);
      }
    });

    const formatted = wasteCategoryList.map((item) => {
      return {
        id: item.idWasteCategory,
        name: item.wasteCategoryName,
        products: []
      };
    });
    setFieldValue('categories', formatted);
    setFieldValue('categoriesLoaded', true);
  };

  React.useEffect(() => {
    localforage.getItem<UserInterface>(LocalStorageKeyEnum['USER'], (_, user) => {
      const code = new URLSearchParams(location.search).get('code');
      if (user && !code) {
        setFieldValue('userId', user.userId);
        setFieldValue('name', user.name);
        setFieldValue('outletId', user.outletId);
        setFieldValue('outletName', user.outletName);
        setFieldValue('startTime', user.startTime);
        setPageStatus('success');
        fetchWasteCategory();
      } else {
        localforage.getItem(LocalStorageKeyEnum['USER']);
        onCheckUserLark(code).then(() => {
          fetchWasteCategory();
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, onCheckUserLark, setFieldValue]);

  React.useEffect(() => {
    const unloadCallback = () => {
      localforage.getItem<UserInterface>(LocalStorageKeyEnum['USER'], (err, user) => {
        localforage.setItem<UserInterface>(LocalStorageKeyEnum['USER'], user);
      });
    };

    window.addEventListener('beforeunload', unloadCallback);
    return () => window.removeEventListener('beforeunload', unloadCallback);
  }, []);

  React.useEffect(() => {
    const onBackButtonEvent = () => history.push('/');

    window.history.pushState(null, null, window.location.pathname);
    window.addEventListener('popstate', onBackButtonEvent);
    return () => window.removeEventListener('popstate', onBackButtonEvent);
  }, [history]);

  const checkNetwork = () => (
    <Detector
      render={({ online }) => {
        if (!online) {
          return (
            <div className="flex justify-center items-center bg-danger-default w-full h-9">
              <div className="text-regular text-white font-interLight">
                Tidak ada koneksi internet
              </div>
            </div>
          );
        } else {
          if (values.isOnline) {
            return (
              <div className="flex justify-center items-center bg-positive-lighter w-full h-9">
                <div className="text-regular text-primary-green font-interLight">
                  Koneksi kembali stabil
                </div>
              </div>
            );
          }
          return <div />;
        }
      }}
      onChange={(value: boolean) => {
        if (value) {
          setFieldValue('isOnline', true);
          setTimeout(() => setFieldValue('isOnline', false), 3000);
        }
      }}
    />
  );

  const productChecker = React.useCallback(() => {
    const checker = values.categories?.filter((category) => category.products?.length > 0);
    if (checker?.length > 0) {
      setFieldValue('buttonDisabled', false);
    } else {
      setFieldValue('buttonDisabled', true);
    }
  }, [values.categories, setFieldValue]);

  React.useEffect(() => {
    productChecker();
  }, [productChecker]);

  const toggleModalAccountVisible = () =>
    setFieldValue('isModalAccountVisible', !values.isModalAccountVisible);
  const toggleModalConfirmationVisible = () =>
    setFieldValue('isModalConfirmationVisible', !values.isModalConfirmationVisible);
  const toggleModalSubmitFailed = () =>
    setFieldValue('isModalErrorSubmitVisible', !values.isModalErrorSubmitVisible);
  const closeModalBackground = () => null;

  return (
    <div className="bg-gray-gray200 min-h-screen bg-opacity-100">
      <Header title="Waste Form" />
      <Layout>
        <div>
          {pageStatus === 'loading' ? (
            <div className="min-h-screen justify-center flex">
              <Loader />
            </div>
          ) : pageStatus === 'success' ? (
            <>
              <ModalAccountInfo
                outletName={values.outletName}
                outletCrew={values.name}
                dateInput={`${dateFormatter(
                  new Date(values?.startTime),
                  'EEEE, dd MMMM yyyy'
                )} / ${dateFormatter(new Date(values?.startTime), 'HH:mm')} WIB`}
                cancelButtonRef={cancelButtonRef}
                isShowModal={values.isModalAccountVisible}
                closeModal={toggleModalAccountVisible}
                closeModalBackground={closeModalBackground}
              />
              <ModalConfirmation
                icon="/images/warning.svg"
                title="Submit Form Waste?"
                description="Pastikan kamu sudah mengisi form waste dengan benar sebelum melakukan submit"
                onClick={handleSubmit}
                cancelButtonRef={cancelButtonRef}
                isShowModal={values.isModalConfirmationVisible}
                isLoading={values.isLoadingSubmit}
                closeModal={toggleModalConfirmationVisible}
                closeModalBackground={closeModalBackground}
                variantButton="DOUBLE"
                textLeft="Batal"
                textRight="Ya, submit"
              />
              <ModalConfirmation
                icon="/images/warning.svg"
                title="Gagal Melakukan Submit"
                description="Ups, dokumen yang kamu input gagal terkirim. Silahkan tutup halaman ini dan coba masukkan ulang dokumen waste yang ingin kamu buat."
                cancelButtonRef={cancelButtonRef}
                isShowModal={values.isModalErrorSubmitVisible}
                closeModalBackground={closeModalBackground}
                variantButton="SINGLE"
                textCenter="Oke, mengerti"
                onClick={toggleModalSubmitFailed}
              />
              <HeaderOutlet outlet="Yummy Kitchen" infoFormOnClick={toggleModalAccountVisible} />
              <div className="w-full sticky mt-2">{checkNetwork()}</div>
              <BodyWrapper>
                <div className="font-interMedium text-extraLarge mt-2 ml-2">Pilih Kategori</div>
                <section className="mt-5">
                  {values.categories?.map((category: any, index) => (
                    <CardCategory
                      key={index}
                      category={category}
                      onClick={() => history.push(`add-product/${category.name}?id=${category.id}`)}
                    />
                  ))}
                </section>
                <div style={{ height: 110 }} className="h-110"></div>
              </BodyWrapper>
              <div className="fixed bottom-0 w-full p-4 bg-white">
                <Button
                  isDisabled={values.buttonDisabled}
                  onClick={toggleModalConfirmationVisible}
                  label="Submit"
                />
              </div>
            </>
          ) : pageStatus === 'failed' ? (
            <div className="flex items-center justify-center min-h-screen">
              <div className="m-auto">
                <div className="text-center mx-4">
                  <div className="text-large text-gray-gray700 font-interSemiBold mb-3">
                    {values.isServerError
                      ? 'Sistem Sedang Mengalami Gangguan'
                      : 'Form Waste Belum Tersedia'}
                  </div>
                  <div className="text-regular text-gray-gray600 font-interLight">
                    {values.isServerError ? (
                      'Silahkan mencoba lagi dengan cara membuka form waste kembali melalui menu Workplace'
                    ) : (
                      <div>
                        Pengisian form hanya dapat dilakukan mulai dari pukul
                        <span className="text-regular text-gray-gray700 font-interSemiBold ml-1">
                          13.00 - 23.00
                        </span>
                        .
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <Fragment />
          )}
        </div>
      </Layout>
    </div>
  );
};

export default Home;
